'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FileText, Search } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { getClientAnalytics } from '@naumu/analytics/analytics/client-analytics';
import { Backdrop } from '@naumu/ui/components/backdrop';
import { Input } from '@naumu/ui/components/input';
import {
	flattenNavItems,
	type DocMeta,
	type DocsNavGroup,
} from '@/modules/docs/helpers/docs-loader';
import { cn } from '@naumu/ui/utils/cn';

const matches = (doc: DocMeta, query: string): boolean => {
	const haystack = `${doc.title} ${doc.description}`.toLowerCase();
	const needle = query.toLowerCase().trim();
	if (!needle) return true;
	// Subsequence-tolerant: every whitespace-separated term must appear.
	return needle.split(/\s+/).every((term) => haystack.includes(term));
};

export const DocsSearch = ({
	navGroups,
	open,
	onClose,
}: {
	navGroups: DocsNavGroup[];
	open: boolean;
	onClose: () => void;
}): React.JSX.Element => {
	const router = useRouter();
	const [query, setQuery] = useState('');
	const [selectedIndex, setSelectedIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);

	// Subpages are searchable too, so flatten each group's nested items.
	const allDocs = useMemo(
		() => navGroups.flatMap((group) => flattenNavItems(group.items)),
		[navGroups]
	);

	const results = useMemo(
		() => allDocs.filter((doc) => matches(doc, query)),
		[allDocs, query]
	);

	// Fire docs_search_queried once per open session. Reads live query/results
	// via a ref so the fire helper stays stable and the close handler below can
	// use it without re-subscribing. `firedRef` resets on each open.
	const stateRef = useRef({ query, resultsCount: results.length });
	stateRef.current = { query, resultsCount: results.length };
	const firedRef = useRef(false);

	const fireSearch = useCallback((selectedSlug: string | null) => {
		if (firedRef.current) return;
		const trimmed = stateRef.current.query.trim();
		if (!trimmed) return; // never fire for empty queries
		firedRef.current = true;
		getClientAnalytics().track('docs_search_queried', {
			query: trimmed,
			results_count: stateRef.current.resultsCount,
			selected_slug: selectedSlug,
		});
	}, []);

	// Reset the per-session guard on open; on close, fire the abandon event for
	// any non-empty query the reader didn't act on. Selection paths fire first
	// (setting the guard) before onClose flips `open`, so this never double-counts
	// and covers every close path (Escape, backdrop, Cmd+K toggle, route change).
	useEffect(() => {
		if (open) {
			firedRef.current = false;
			return;
		}
		fireSearch(null);
	}, [open, fireSearch]);

	// Reset query/selection and focus the field whenever the palette opens.
	useEffect(() => {
		if (open) {
			setQuery('');
			setSelectedIndex(0);
			// Defer focus until after the panel paints.
			const id = requestAnimationFrame(() => inputRef.current?.focus());
			return () => cancelAnimationFrame(id);
		}
	}, [open]);

	// Keep the highlighted row in range as the result set narrows.
	useEffect(() => {
		setSelectedIndex(0);
	}, [query]);

	// Escape closes; arrows move the selection; Enter opens it.
	useEffect(() => {
		if (!open) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				onClose();
			} else if (event.key === 'ArrowDown') {
				event.preventDefault();
				setSelectedIndex((prev) => Math.min(results.length - 1, prev + 1));
			} else if (event.key === 'ArrowUp') {
				event.preventDefault();
				setSelectedIndex((prev) => Math.max(0, prev - 1));
			} else if (event.key === 'Enter') {
				const target = results[selectedIndex];
				if (target) {
					event.preventDefault();
					fireSearch(target.slug);
					router.push(`/docs/${target.slug}`);
					onClose();
				}
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [open, onClose, results, selectedIndex, router, fireSearch]);

	if (!open) return <></>;

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label="Search docs"
			className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]"
		>
			<Backdrop variant="faint" onClick={onClose} />

			<div className="relative z-10 mx-4 flex w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-border-weak bg-overlay shadow-2xl backdrop-blur-xl animate-in fade-in-0 zoom-in-95 slide-in-from-top-4 duration-200">
				<div className="flex items-center gap-3 border-b border-border-weak px-4 py-3">
					<Search className="size-5 shrink-0 text-neutral-muted" strokeWidth={2} />
					<Input
						ref={inputRef}
						variant="ghost"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder="Search docs"
						aria-label="Search docs"
					/>
					<kbd className="rounded-sm border border-border-weak bg-background px-1.5 py-0.5 text-[11px] font-semibold text-neutral-muted">
						Esc
					</kbd>
				</div>

				<div className="max-h-[50vh] overflow-y-auto p-2">
					{results.length === 0 ? (
						<div className="px-3 py-8 text-center text-[13px] text-neutral-muted">
							No results for &ldquo;{query}&rdquo;
						</div>
					) : (
						results.map((doc, index) => (
							<Link
								key={doc.slug}
								href={`/docs/${doc.slug}`}
								onClick={() => {
									fireSearch(doc.slug);
									onClose();
								}}
								onMouseEnter={() => setSelectedIndex(index)}
								aria-selected={index === selectedIndex}
								className={cn(
									'flex items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
									index === selectedIndex
										? 'bg-surface-strong text-neutral'
										: 'text-neutral hover:bg-surface'
								)}
							>
								<FileText
									className="mt-0.5 size-4 shrink-0 text-neutral-muted"
									strokeWidth={2}
								/>
								<div className="flex min-w-0 flex-col">
									<span className="truncate text-[13px] font-medium text-neutral">
										{doc.title}
									</span>
									{doc.description && (
										<span className="truncate text-[11px] text-neutral-muted">
											{doc.description}
										</span>
									)}
								</div>
							</Link>
						))
					)}
				</div>

				<div className="border-t border-border-weak bg-surface px-4 py-2">
					<div className="flex items-center gap-4 text-[11px] text-neutral-muted">
						<span className="flex items-center gap-1">
							<kbd className="rounded-sm border border-border-weak bg-background px-1 py-0.5">
								↑
							</kbd>
							<kbd className="rounded-sm border border-border-weak bg-background px-1 py-0.5">
								↓
							</kbd>
							<span>Navigate</span>
						</span>
						<span className="flex items-center gap-1">
							<kbd className="rounded-sm border border-border-weak bg-background px-1 py-0.5">
								↵
							</kbd>
							<span>Select</span>
						</span>
					</div>
				</div>
			</div>
		</div>
	);
};
